/* eslint-disable react-refresh/only-export-components */
import React, { useCallback, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import {
    ArticleOutlined as SnippetIcon,
    OpenInNew as OpenInNewIcon,
    MenuBook as BookIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { isSafeAttachmentUrl } from '../../utils/isSafeAttachmentUrl';
import { isInAppHref, isAllowedExternalHelpHref } from '../support-hub/tabs/AIAssistantTab';
import type { CitedSource, DocUrlResponse, KnowledgeLayer } from './types';

const LAYER_LABELS: Record<string, string> = {
    MANUFACTURER: 'Manual',
    MANUAL: 'Manual',
    STANDARD: 'Standard',
    STANDARDS: 'Standard',
    COMPLIANCE: 'Compliance',
    GENERAL: 'General',
    HELP_ARTICLE: 'Help',
};

function stripRawCitationTags(text: string): string {
    return text.replace(/\[\^?(?:A|S)?\d+\]/g, '').trim();
}

export function isSafeInAppPath(href: string | null | undefined): boolean {
    if (!href || typeof href !== 'string') return false;
    if (!/^\/[^/\\]/.test(href)) return false;
    return isInAppHref(href);
}

export function isSafeExternalUrl(href: string | null | undefined): boolean {
    if (!href || typeof href !== 'string') return false;
    return isAllowedExternalHelpHref(href);
}

export function formatSourceChipLabel(source: CitedSource, index?: number): string {
    const rawKind = (source.kind ?? source.layer ?? '').toUpperCase();
    const isHelpArticle = rawKind === 'HELP_ARTICLE' || (!source.kind && !source.layer && Boolean(source.url));
    const layerLabel = !isHelpArticle && rawKind ? LAYER_LABELS[rawKind as KnowledgeLayer] ?? rawKind : null;

    const rawTitle = stripRawCitationTags(source.title || '');
    const versionLabel = source.version_label ? stripRawCitationTags(source.version_label) : null;
    const titleWithVersion =
        versionLabel && !rawTitle.includes(versionLabel)
            ? `${rawTitle} ${versionLabel}`
            : rawTitle;

    const clausePart = source.clause_ref
        ? source.clause_ref.toLowerCase().startsWith('cl')
            ? source.clause_ref
            : `cl. ${source.clause_ref}`
        : null;

    const pageLabel = source.printed_page_label
        ? `p.${source.printed_page_label}`
        : typeof source.page_number === 'number' && source.page_number > 0
          ? `p.${source.page_number}`
          : null;

    const detail = [titleWithVersion, clausePart, pageLabel].filter(Boolean).join(' ');
    const labelBody = [layerLabel, detail].filter(Boolean).join(' · ');

    if (typeof index === 'number') {
        return `${index + 1}. ${labelBody}`;
    }
    return labelBody;
}

export interface SourceChipsProps {
    sources: CitedSource[];
    sessionId?: string | null;
    onFetchDocUrl?: (documentId: string, pageNumber?: number) => Promise<DocUrlResponse>;
    numbered?: boolean;
}

export const SourceChips: React.FC<SourceChipsProps> = ({
    sources,
    sessionId,
    onFetchDocUrl,
    numbered = false,
}) => {
    const [loadingRef, setLoadingRef] = useState<string | null>(null);
    const [modalSource, setModalSource] = useState<CitedSource | null>(null);

    const handleClickSource = useCallback(
        async (source: CitedSource, sourceKey: string) => {
            const rawKind = (source.kind ?? source.layer ?? '').toUpperCase();
            const isStandard = rawKind === 'STANDARD' || rawKind === 'STANDARDS';
            const isSnippetOnly = isStandard || source.display_policy === 'SNIPPET_ONLY';

            // 1. STANDARD / SNIPPET_ONLY licence guard BEFORE any URL handling: standard is never a link
            if (isSnippetOnly) {
                if (source.snippet?.trim() || source.description?.trim()) {
                    setModalSource(source);
                }
                return;
            }

            // 2. Safe URL handling for in-app or allowed external URLs
            if (source.url) {
                if (isSafeInAppPath(source.url)) {
                    window.location.href = source.url;
                    return;
                }
                if (isSafeExternalUrl(source.url)) {
                    window.open(source.url, '_blank', 'noopener,noreferrer');
                    return;
                }
                // Reject unsafe URLs (//, /\, javascript:, unlisted hosts)
            }

            // 3. MANUAL source: must have document_id to fetch doc_url
            if (!source.document_id || (!sessionId && !onFetchDocUrl)) {
                if (source.snippet?.trim() || source.description?.trim()) {
                    setModalSource(source);
                }
                return;
            }

            setLoadingRef(sourceKey);
            try {
                let result: DocUrlResponse | null = null;
                if (onFetchDocUrl) {
                    result = await onFetchDocUrl(source.document_id, source.page_number ?? undefined);
                } else {
                    const payload: Record<string, unknown> = {
                        action: 'doc_url',
                        session_id: sessionId ?? '',
                        document_id: source.document_id,
                    };
                    if (typeof source.page_number === 'number' && source.page_number >= 1) {
                        payload.page_number = source.page_number;
                    }
                    const { data } = await supabase.functions.invoke('expert-chat', {
                        body: payload,
                    });
                    result = (data as DocUrlResponse | null) ?? null;
                }

                if (
                    result &&
                    (result.kind === 'OUR_COPY' || result.kind === 'SOURCE_URL') &&
                    result.url &&
                    isSafeAttachmentUrl(result.url)
                ) {
                    window.open(result.url, '_blank', 'noopener,noreferrer');
                } else if (source.snippet?.trim() || source.description?.trim()) {
                    setModalSource(source);
                }
            } catch {
                if (source.snippet?.trim() || source.description?.trim()) {
                    setModalSource(source);
                }
            } finally {
                setLoadingRef(null);
            }
        },
        [onFetchDocUrl, sessionId],
    );

    if (!sources || sources.length === 0) return null;

    return (
        <Box sx={{ mt: 1.5 }}>
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: 0.4,
                    display: 'block',
                    mb: 0.75,
                }}
            >
                Sources
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {sources.map((source, index) => {
                    const rawKind = (source.kind ?? source.layer ?? '').toUpperCase();
                    const isStandard = rawKind === 'STANDARD' || rawKind === 'STANDARDS';
                    const isHelpArticle = rawKind === 'HELP_ARTICLE' || (!source.kind && !source.layer && Boolean(source.url));
                    const isManual = !isStandard && !isHelpArticle;
                    const canOpenDoc = Boolean(sessionId || onFetchDocUrl);

                    const label = formatSourceChipLabel(source, numbered ? index : undefined);
                    const sourceKey = `${source.ref ?? index}-${source.document_id ?? ''}-${source.page_number ?? ''}`;
                    const isBusy = loadingRef === sourceKey;
                    const isSnippetOnly = isStandard || source.display_policy === 'SNIPPET_ONLY';
                    const hasSnippetText = Boolean(source.snippet?.trim() || source.description?.trim());

                    // L2: Standard/snippet-only is NEVER a link
                    const hasInAppUrl = !isSnippetOnly && Boolean(source.url && isSafeInAppPath(source.url));
                    const hasExternalUrl = !isSnippetOnly && Boolean(source.url && isSafeExternalUrl(source.url));

                    // L3: A STANDARD chip with no snippet text is not clickable
                    const isClickable =
                        hasInAppUrl ||
                        hasExternalUrl ||
                        (isSnippetOnly ? hasSnippetText : isManual ? canOpenDoc || hasSnippetText : false);

                    const chipProps: Record<string, unknown> = {
                        label,
                        variant: 'outlined',
                        clickable: isClickable,
                        disabled: isBusy,
                        size: 'small',
                        sx: {
                            height: 'auto',
                            py: 0.5,
                            cursor: isClickable ? 'pointer' : 'default',
                            '& .MuiChip-label': {
                                whiteSpace: 'normal',
                                fontWeight: 600,
                            },
                        },
                    };

                    if (hasInAppUrl) {
                        return (
                            <Chip
                                key={sourceKey}
                                {...chipProps}
                                component={Link}
                                to={source.url!}
                                aria-label={stripRawCitationTags(source.title || label)}
                                icon={<OpenInNewIcon fontSize="small" />}
                            />
                        );
                    }

                    if (hasExternalUrl) {
                        return (
                            <Chip
                                key={sourceKey}
                                {...chipProps}
                                component="a"
                                href={source.url!}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={stripRawCitationTags(source.title || label)}
                                icon={<OpenInNewIcon fontSize="small" />}
                            />
                        );
                    }

                    return (
                        <Chip
                            key={sourceKey}
                            {...chipProps}
                            aria-label={`Source: ${label}`}
                            onClick={isClickable ? () => void handleClickSource(source, sourceKey) : undefined}
                            icon={
                                !isClickable ? undefined : isBusy ? (
                                    <CircularProgress size={14} />
                                ) : isSnippetOnly ? (
                                    <SnippetIcon fontSize="small" />
                                ) : isManual && !canOpenDoc ? (
                                    <BookIcon fontSize="small" />
                                ) : (
                                    <OpenInNewIcon fontSize="small" />
                                )
                            }
                        />
                    );
                })}
            </Stack>

            <Dialog
                open={modalSource !== null}
                onClose={() => setModalSource(null)}
                maxWidth="sm"
                fullWidth
            >
                {modalSource && (
                    <>
                        <DialogTitle>
                            <Typography
                                variant="caption"
                                color="primary"
                                sx={{ fontWeight: 700, textTransform: 'uppercase' }}
                            >
                                {LAYER_LABELS[modalSource.kind ?? modalSource.layer ?? 'MANUAL'] ?? modalSource.layer}
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                {stripRawCitationTags(modalSource.title)}
                                {modalSource.version_label
                                    ? ` (${stripRawCitationTags(modalSource.version_label)})`
                                    : ''}
                            </Typography>
                        </DialogTitle>
                        <DialogContent dividers>
                            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1.5 }}>
                                {modalSource.clause_ref && (
                                    <Chip
                                        size="small"
                                        variant="outlined"
                                        label={
                                            modalSource.clause_ref.toLowerCase().startsWith('cl')
                                                ? modalSource.clause_ref
                                                : `cl. ${modalSource.clause_ref}`
                                        }
                                    />
                                )}
                                {(modalSource.printed_page_label ||
                                    (typeof modalSource.page_number === 'number' && modalSource.page_number > 0)) && (
                                    <Chip
                                        size="small"
                                        variant="outlined"
                                        label={
                                            modalSource.printed_page_label
                                                ? `p. ${modalSource.printed_page_label}`
                                                : `p. ${modalSource.page_number}`
                                        }
                                    />
                                )}
                            </Stack>

                            <Alert severity="info" sx={{ mb: 2 }}>
                                Full text isn&apos;t linked directly for this source. Showing the
                                cited excerpt and reference below.
                            </Alert>

                            <Paper
                                variant="outlined"
                                sx={{ p: 2, bgcolor: 'background.default' }}
                            >
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {modalSource.snippet || modalSource.description || 'No excerpt text provided.'}
                                </Typography>
                            </Paper>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setModalSource(null)}>Close</Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </Box>
    );
};